import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../services/api";
import { SharedClipResponse } from "../../types/api";
import { SHARE_CONFIG } from "../../config/constants";
import Spinner from "../Spinner";
import { FamflixMark, ReplayIcon, ClockIcon } from "../icons";
import InfoScreen from "../InfoScreen";
import VideoFrame from "./VideoFrame";
import { useI18n } from "../../i18n";

type Phase = "loading" | "processing" | "ready" | "error";

export default function SharedClipPage() {
  const { t } = useI18n();
  const { token } = useParams<{ token: string }>();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [phase, setPhase] = useState<Phase>("loading");
  const [clip, setClip] = useState<SharedClipResponse | null>(null);
  const [isEnded, setIsEnded] = useState(false);

  // Resolve the clip, polling while the render is still in flight. Gives up at
  // the deadline so a stuck render surfaces an error instead of spinning.
  useEffect(() => {
    if (!token) {
      setPhase("error");
      return;
    }

    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + SHARE_CONFIG.pollTimeoutMs;

    const poll = () => {
      apiService
        .getSharedClip(token)
        .then((result: SharedClipResponse) => {
          if (!active) return;
          if (result.status === "ready" && result.url) {
            setClip(result);
            setPhase("ready");
          } else if (result.status === "processing" && Date.now() < deadline) {
            setPhase("processing");
            timer = setTimeout(poll, SHARE_CONFIG.pollIntervalMs);
          } else {
            setPhase("error");
          }
        })
        .catch(() => {
          if (active) setPhase("error");
        });
    };

    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [token]);

  const replay = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      setIsEnded(false);
      void video.play();
    }
  };

  if (phase === "loading") {
    return <Spinner text={t("shared.loading")} />;
  }

  if (phase === "processing") {
    return (
      <InfoScreen
        icon={<span className="loading loading-ring loading-lg text-primary" />}
        title={t("shared.processingTitle")}
      >
        <p className="opacity-70">{t("shared.processingBody")}</p>
      </InfoScreen>
    );
  }

  if (phase === "error" || !clip?.url) {
    return (
      <InfoScreen
        icon={<ClockIcon className="w-12 h-12 opacity-70" />}
        title={t("shared.errorTitle")}
      >
        <p className="opacity-70">{t("shared.errorBody")}</p>
      </InfoScreen>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center justify-center p-4 gap-4">
      <div className="w-full max-w-4xl">
        <div className="flex items-baseline justify-between mb-3">
          <span className="flex items-center gap-2 text-primary">
            <FamflixMark className="w-7 h-7" />
            <span className="text-xl font-bold text-base-content font-display tracking-tight">
              Famflix
            </span>
          </span>
          <span className="text-sm opacity-60">{t("shared.clipLabel")}</span>
        </div>

        {clip.title && (
          <p className="text-base-content/80 mb-2">{clip.title}</p>
        )}

        <VideoFrame>
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full"
            controls
            autoPlay
            playsInline
            src={clip.url}
            onEnded={() => setIsEnded(true)}
          />

          {isEnded && (
            <button
              onClick={replay}
              className="absolute inset-0 flex items-center justify-center bg-black/60 text-white gap-2"
            >
              <ReplayIcon className="w-6 h-6" />
              {t("shared.replay")}
            </button>
          )}
        </VideoFrame>

        <p className="text-xs opacity-50 mt-3 text-center">
          {t("shared.footer")}
        </p>
      </div>
    </div>
  );
}
